// L'espace de modération — réservé aux comptes `moderateur` et `admin`.
//
// Quatre onglets, quatre travaux différents : les signalements à traiter, ceux
// qui l'ont été, les attestations de baptême qui attendent une décision, et les
// membres suspendus.
//
// Une chose ici n'est pas de l'affichage mais une promesse tenue : le délai de
// validation annoncé au membre à l'inscription. Quand il est sur le point d'être
// rompu, la ligne le dit — c'est le seul garde-fou quand une seule personne
// valide et qu'un dimanche chargé peut tout faire glisser.

import { useEffect, useState } from "react";
import Store from "../../store.js";
import { APP } from "../../config.js";
import { ilYA, toast, confirmer } from "../../ui.js";
import { estModerateur, suspendreMembre } from "../../views/moderation-outils.js";
import Avatar from "../Avatar.jsx";
import Certifie from "../Certifie.jsx";
import Icone from "../Icone.jsx";
import TexteRiche from "../TexteRiche.jsx";
import Vide from "../Vide.jsx";
import { useAbonnement } from "../hooks.js";

const ONGLETS = [
  { id: "attente", label: "À traiter" },
  { id: "traite", label: "Traités" },
  { id: "baptemes", label: "Baptêmes" },
  { id: "suspendus", label: "Suspendus" },
];

const LIBELLE_CIBLE = {
  publication: "une publication",
  profil: "un profil",
  commentaire: "un commentaire",
  statut: "un statut",
};

/* ------------------------------------------------------------- signalements */

/**
 * L'aperçu du contenu visé.
 *
 * Chargé APRÈS la ligne, et pas avec elle : un modérateur doit voir sa liste
 * tout de suite, quitte à ce que les aperçus arrivent une seconde plus tard.
 */
function ApercuContenu({ signalement }) {
  const [post, setPost] = useState(undefined); // undefined = pas encore demandé

  useEffect(() => {
    if (signalement.cibleType !== "publication") return;
    let vivant = true;
    Store.posts
      .get(signalement.cibleId)
      .catch(() => null)
      .then((p) => vivant && setPost(p));
    return () => {
      vivant = false;
    };
  }, [signalement.cibleId, signalement.cibleType]);

  if (signalement.cibleType !== "publication" || post === undefined) return null;
  if (!post) return <p className="petit texte-doux">Contenu déjà supprimé.</p>;

  return (
    <div className="apercu-contenu">
      {post.texte ? <TexteRiche texte={post.texte} /> : <em>Sans texte</em>}
      {post.image && <img src={post.image} alt="" loading="lazy" />}
      {post.video && <video src={post.video} controls playsInline preload="metadata" />}
    </div>
  );
}

function Signalement({ s, profils }) {
  const auteur = profils.get(s.auteurId);
  const rapporteur = profils.get(s.parId);

  const supprimer = async () => {
    if (
      !(await confirmer(
        "Supprimer ce contenu ?",
        "Il disparaîtra pour tous les membres. Le signalement sera marqué comme traité.",
        "Supprimer"
      ))
    )
      return;
    try {
      if (s.cibleType === "publication") await Store.posts.remove(s.cibleId);
      else if (s.cibleType === "statut") await Store.stories.remove(s.cibleId);
      await Store.reports.traiter(s.id, "supprime");
      toast("Contenu supprimé");
    } catch (err) {
      toast(err.message || "Suppression impossible", "erreur");
    }
  };

  return (
    <article className={`signalement${s.traite ? " est-traite" : ""}`}>
      <header className="signalement__tete">
        <span className="signalement__motif">
          <Icone nom="options" taille={16} /> {s.motif}
        </span>
        <span className="petit texte-doux">{ilYA(s.createdAt)}</span>
      </header>

      <p className="signalement__quoi">
        Signalement concernant {LIBELLE_CIBLE[s.cibleType] || s.cibleType}
        {auteur && (
          <>
            {" de "}
            <a href={`#/profil/${s.auteurId}`}>
              <strong>{auteur.nom}</strong>
            </a>
          </>
        )}
        {rapporteur && ` — signalé par ${rapporteur.nom}`}
      </p>

      {s.detail && (
        <blockquote className="apercu">
          <TexteRiche texte={s.detail} />
        </blockquote>
      )}

      <div className="signalement__contenu">
        <ApercuContenu signalement={s} />
      </div>

      {s.traite ? (
        <p className="petit texte-doux">
          ✔ Traité —{" "}
          {s.decision === "supprime"
            ? "contenu supprimé"
            : s.decision === "suspendu"
            ? "auteur suspendu"
            : "signalement rejeté"}
        </p>
      ) : (
        <div className="signalement__actions">
          {s.cibleType === "publication" && (
            <a className="btn btn--gris btn--petit" href={`#/publication/${s.cibleId}`}>
              Voir en contexte
            </a>
          )}
          {s.auteurId && (
            <a className="btn btn--gris btn--petit" href={`#/profil/${s.auteurId}`}>
              Voir le profil
            </a>
          )}
          <button className="btn btn--danger btn--petit" onClick={supprimer}>
            Supprimer le contenu
          </button>
          {s.auteurId && auteur && (
            <button
              className="btn btn--danger btn--petit"
              /* ⚠️ « suspendu », pas « supprime » — 20 octobre 2026, trouvé à
                 l'audit : suspendre marquait le signalement « contenu
                 supprimé » alors que rien n'avait été supprimé. Une décision
                 doit dire ce qu'elle a fait. */
              onClick={() => suspendreMembre(auteur, () => Store.reports.traiter(s.id, "suspendu"))}
            >
              Suspendre l'auteur
            </button>
          )}
          <button
            className="btn btn--gris btn--petit"
            onClick={async () => {
              await Store.reports.traiter(s.id, "rejete");
              toast("Signalement rejeté");
            }}
          >
            Rejeter
          </button>
        </div>
      )}
    </article>
  );
}

/* ---------------------------------------------------------------- baptêmes */

function Bapteme({ u }) {
  const b = u.bapteme || {};
  const attente = Date.now() - (u.createdAt || Date.now());
  const limite = (APP.delaiValidationHeures || 24) * 3600 * 1000;
  const retard = attente > limite;
  const bientot = !retard && attente > limite * 0.75;

  /* ⚠️ DEUX GÉNÉRATIONS DE DOSSIERS COHABITENT, ET IL FAUT LIRE LES DEUX.
     Avant le 18 août 2026, on demandait une photo d'attestation ; depuis, on
     demande une réponse et un nom. Les inscriptions déposées avant ce jour ont
     encore leur photo et attendent une décision : cet écran doit continuer de
     la montrer, sinon on validerait à l'aveugle des gens qui ont fourni ce
     qu'on leur demandait. */
  const precisions =
    [
      b.baptise === true ? "Se déclare baptisé" : b.baptise === false ? "Se déclare NON baptisé" : "",
      b.eglise && "à " + b.eglise,
      b.date && "le " + new Date(b.date).toLocaleDateString("fr-FR"),
    ]
      .filter(Boolean)
      .join(" ") || "Aucune précision donnée";

  return (
    <article className={`signalement${retard ? " signalement--retard" : ""}`}>
      <header className="signalement__tete">
        <span className="signalement__motif">
          <Icone nom="membres" taille={16} /> {u.nom}
        </span>
        <span className={`petit ${retard ? "texte-retard" : "texte-doux"}`}>
          inscrit {ilYA(u.createdAt)}
          {retard ? " · délai dépassé" : bientot ? " · délai bientôt atteint" : ""}
        </span>
      </header>

      <p className="signalement__quoi">{precisions}</p>

      {/* ⚠️ C'EST LA PIÈCE MAÎTRESSE DU DOSSIER, depuis qu'il n'y a plus de
          photo : le nom de celui qui a fait connaître Koinonia. Aucune machine
          ne l'a vérifié, personne n'a été prévenu — c'est à vous de
          reconnaître, ou non, un lien réel. */}
      {b.connuPar ? (
        <p className="signalement__quoi">
          <strong>Connu par :</strong> {b.connuPar}
        </p>
      ) : (
        <p className="petit texte-doux">Personne n'est nommée.</p>
      )}

      {/* Les dossiers d'avant le 18 août 2026 portent encore une attestation. */}
      {b.photo && (
        <>
          <div className="apercu-contenu">
            <img src={b.photo} alt="Attestation de baptême" loading="lazy" />
          </div>
          <p className="petit texte-doux">
            Attestation déposée avant le 18 août 2026. Elle sera effacée dès votre décision.
          </p>
        </>
      )}

      <div className="signalement__actions">
        <a className="btn btn--gris btn--petit" href={`#/profil/${u.uid}`}>
          Voir le profil
        </a>
        <button
          className="btn btn--primaire btn--petit"
          onClick={async () => {
            await Store.moderation.deciderBapteme(u.uid, true);
            toast(`${u.nom} peut désormais publier 🙌`);
          }}
        >
          Valider
        </button>
        <button
          className="btn btn--danger btn--petit"
          onClick={async () => {
            if (
              await confirmer(
                "Refuser cette attestation ?",
                `${u.nom} pourra toujours lire, mais pas publier. La photo sera effacée.`,
                "Refuser"
              )
            ) {
              await Store.moderation.deciderBapteme(u.uid, false, "Attestation illisible ou non conforme");
              toast("Attestation refusée");
            }
          }}
        >
          Refuser
        </button>
      </div>
    </article>
  );
}

/* --------------------------------------------------------------- suspendus */

function Suspendu({ u }) {
  const definitive = u.suspendu > Date.now() + 3650 * 864e5;
  return (
    <div className="membre">
      <a className="membre__qui" href={`#/profil/${u.uid}`}>
        <Avatar membre={u} taille={46} />
        <span>
          <strong>
            {u.nom}
            <Certifie membre={u} taille={13} />
          </strong>
          <span className="petit texte-doux">
            Suspendu{" "}
            {definitive ? "définitivement" : "jusqu'au " + new Date(u.suspendu).toLocaleDateString("fr-FR")}
            {u.suspenduMotif ? " — " + u.suspenduMotif : ""}
          </span>
        </span>
      </a>
      <button
        className="btn btn--gris btn--petit"
        onClick={async () => {
          if (await confirmer("Réactiver ce membre ?", "Il pourra de nouveau publier et échanger.", "Réactiver")) {
            await Store.moderation.reactiver(u.uid);
            toast("Membre réactivé");
          }
        }}
      >
        Réactiver
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------- écran */

export default function Moderation({ ctx }) {
  const [onglet, setOnglet] = useState("attente");
  const [profils, setProfils] = useState(new Map());

  // Ces trois abonnements ne s'installent QUE pour un modérateur : c'est le
  // composant entier qui n'est pas rendu autrement.
  const signalements = useAbonnement((cb) => Store.reports.subscribe(cb), []);
  const aValider = useAbonnement((cb) => Store.moderation.subscribeAValider(cb), []);
  const suspendus = useAbonnement((cb) => Store.moderation.subscribeSuspendus(cb), []);

  // Les profils cités dans les signalements, récupérés une seule fois chacun.
  useEffect(() => {
    const ids = new Set();
    for (const s of signalements) {
      if (s.auteurId) ids.add(s.auteurId);
      if (s.parId) ids.add(s.parId);
    }
    const manquants = [...ids].filter((id) => !profils.has(id));
    if (!manquants.length) return;
    let vivant = true;
    (async () => {
      const trouves = await Promise.all(manquants.map(async (id) => [id, await ctx.profil(id)]));
      if (vivant) setProfils((p) => new Map([...p, ...trouves]));
    })();
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signalements]);

  if (!estModerateur(ctx.moi))
    return (
      <div className="colonne">
        <Vide
          symbole="🔒"
          titre="Espace réservé"
          texte="Cette page est réservée aux modérateurs de la communauté."
        />
      </div>
    );

  const listeSignalements = signalements.filter((s) => (onglet === "attente" ? !s.traite : s.traite));

  const corps = () => {
    if (onglet === "baptemes")
      return aValider.length ? (
        <div className="liste-signalements">
          {aValider.map((u) => (
            <Bapteme u={u} key={u.uid} />
          ))}
        </div>
      ) : (
        <Vide symbole="🕊️" titre="Aucune attestation à vérifier" />
      );

    if (onglet === "suspendus")
      return suspendus.length ? (
        <div className="grille-membres">
          {suspendus.map((u) => (
            <Suspendu u={u} key={u.uid} />
          ))}
        </div>
      ) : (
        <Vide symbole="🕊️" titre="Aucun membre suspendu" />
      );

    if (!listeSignalements.length)
      return onglet === "attente" ? (
        <Vide symbole="🕊️" titre="Rien à traiter" texte="La communauté se porte bien." />
      ) : (
        <Vide symbole="📁" titre="Aucun signalement traité" />
      );

    return (
      <div className="liste-signalements">
        {listeSignalements.map((s) => (
          <Signalement s={s} profils={profils} key={s.id} />
        ))}
      </div>
    );
  };

  return (
    <div className="colonne">
      <div className="entete-page">
        <h1 className="titre-page">Modération</h1>
        <div className="filtres" role="tablist">
          {ONGLETS.map((o) => (
            <button
              key={o.id}
              className={`chip${o.id === onglet ? " est-actif" : ""}`}
              onClick={() => setOnglet(o.id)}
            >
              {o.label}
              {o.id === "baptemes" && aValider.length > 0 && (
                <span className="compteur">{aValider.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {corps()}

      <a className="carte lien-politique" href="#/sponsoring">
        <Icone nom="options" taille={20} />
        <span>
          <strong>Politique de sponsoring</strong>
          <span className="petit texte-doux">
            Les règles à appliquer pour accepter ou refuser une mise en avant.
          </span>
        </span>
      </a>
    </div>
  );
}
